import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Star, Settings, Clock } from 'lucide-react';
import { services, whyChooseUs, prestigiousClients } from '../mockData';
import * as LucideIcons from 'lucide-react';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon size={40} /> : null;
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] md:h-[85vh] flex items-center justify-center overflow-hidden mt-24 md:mt-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/7fa87f14-22bf-4541-b3ba-85b19b76e6df/images/bf0ee10e6cc4e5c58849f3b1fa75bbfaf2bbde66b0631ca3856568d690bc2d02.png"
            alt="Man in elegant tuxedo"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center top' }}
          />
          <div className="absolute inset-0 bg-black/55"></div>
        </div>
        
        <div
          className={`relative z-10 text-center px-4 pt-8 md:pt-0 transition-all duration-1000 transform ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <h1
            className="text-3xl md:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: '"Cinzel", serif' }}
            data-testid="hero-title"
          >
            Specialists in Commercial Laundry & Dry Cleaning
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto text-center">
            Leading professional commercial laundry and dry cleaning services across London, specialising in hotel uniforms, chefwear, F&B linens, and bedding.
            <br /><br />
            We support hotels, restaurants, clubs and businesses with reliable service and same day express turnaround.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#1F2D3D] text-white rounded font-medium hover:bg-[#1A2332] transition-all duration-300 hover:shadow-xl hover:scale-105"
            style={{ fontFamily: '"Cinzel", serif' }}
            data-testid="get-started-btn"
          >
            Contact Us
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1F2D3D] mb-6"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              About Us
            </h2>
            <div className="w-24 h-1 bg-[#1F2D3D] mx-auto mb-8"></div>
          </div>
          
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Founded as a family business in London's West End in 1982, we specialise in premium garment care and bring decades of experience serving the hospitality industry seven days a week.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Express services ensure fast turnaround while maintaining exceptional quality and attention to detail. Dry cleaning is carried out at our Wandsworth facility, while our Merton site manages our commercial laundry operations.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We also welcome walk in customers for personal dry cleaning at our Wandsworth facility, conveniently located opposite Battersea Power Station.
            </p>
          </div>
        </div>
      </section>

      {/* Trusted By London's Elite */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1F2D3D] mb-4"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              Trusted By London's Elite
            </h2>
            <div className="w-24 h-1 bg-[#1F2D3D] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Proud partners to prestigious hotels, private members' clubs, and luxury establishments across London. Your garments, their standards.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8 md:gap-12">
            {prestigiousClients.map((client) => {
              // Scale up smaller logos: Bulgari (1), Landmark (3), Flemings (8)
              const needsScaling = [1, 3, 8].includes(client.id);
              const logoClass = needsScaling 
                ? "max-h-28 max-w-full w-auto object-contain scale-125" 
                : "max-h-20 max-w-full w-auto object-contain";
              
              return (
                <div
                  key={client.id}
                  className="flex flex-col items-center justify-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 w-[calc(50%-1rem)] md:w-[calc(33.333%-2rem)] border border-[#1F2D3D]/30 min-h-[180px]"
                >
                  <div className="flex-1 flex items-center justify-center mb-3 min-h-[80px]">
                    {client.logo ? (
                      <img
                        src={client.logo}
                        alt={client.name}
                        className={logoClass}
                      />
                    ) : (
                      <div className="text-center px-2">
                        <h3
                          className="text-[#1F2D3D] font-bold text-base md:text-lg tracking-wider"
                          style={{ fontFamily: '"Cinzel", serif', letterSpacing: '0.08em' }}
                        >
                          {client.name.toUpperCase()}
                        </h3>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm text-center font-medium">
                    {client.displayName}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1F2D3D] mb-4"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              Why Choose Tuxedo Express?
            </h2>
            <div className="w-24 h-1 bg-[#1F2D3D] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = LucideIcons[item.icon];
              return (
                <div
                  key={item.id}
                  className="text-center p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1F2D3D]/10 rounded-full mb-4">
                    {Icon && <Icon className="text-[#1F2D3D]" size={32} />}
                  </div>
                  <h3
                    className="text-xl font-semibold text-[#1F2D3D] mb-2"
                    style={{ fontFamily: '"Cinzel", serif' }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1F2D3D] mb-4"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              Our Services
            </h2>
            <div className="w-24 h-1 bg-[#1F2D3D] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional garment care services tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => {
              const Icon = LucideIcons[service.icon];
              return (
                <div
                  key={service.id}
                  className="group p-8 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1F2D3D] transition-all duration-300 hover:shadow-xl"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[#1F2D3D]/10 rounded-full mb-4 group-hover:bg-[#1F2D3D] transition-colors duration-300">
                    {Icon && (
                      <Icon
                        className="text-[#1F2D3D] group-hover:text-white transition-colors duration-300"
                        size={28}
                      />
                    )}
                  </div>
                  <h3
                    className="text-xl font-semibold text-[#1F2D3D] mb-3"
                    style={{ fontFamily: '"Cinzel", serif' }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-3 bg-[#1F2D3D] text-white rounded font-medium hover:bg-[#1A2332] transition-all duration-300 hover:shadow-lg"
              style={{ fontFamily: '"Cinzel", serif' }}
              data-testid="view-services-btn"
            >
              View All Services
              <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
